import KycApi from '@/api/kyc'
import Config from '@/config'
import { getKycToken, removeKycToken } from '@/utils/auth'
import {
  cacheDob,
  cacheNationalId,
  getCachedDob,
  getCachedNationalId,
  getKycQueryParams,
  getKycSessionId,
  isValidStage,
  KYC_STAGE,
  removeKycSessionId,
  resolveStateFromKycData,
  setKycQueryParams,
  setKycSessionId,
  QUESTIONS_BLACKLISTED,
  FEATURE_GATE_QUESTIONNAIRE_REVAMP,
} from '@/utils/kyc'
import { isFeatureEnabledSafe } from '@/utils/abt'
import logger from '@/utils/logger'
import Seon from '@/utils/seon'
import {
  getKycTokenFromSessionStorage,
  setKycTokenToSessionStorage,
} from '@/utils/sessionStorage'
import parseISO from 'date-fns/parseISO'
import isEmpty from 'lodash/isEmpty'
import isObject from 'lodash/isObject'
import SUPPORTED_CLIENT_TYPE from '@kyc/constants/supported-client-type'
import clientLogger from '@/plugins/logger/core'
import LOGGING_EVENTS from '@/constants/logging-events'
import verificationSteps from '@/constants/verification-steps'
import { getABTProvider } from '@/plugins/abt/statsig'

const abtProvider = getABTProvider()

const DEFAULT_LOCALE_FALLBACK = 'ar_SA'
const SUPPORTED_LOCALE = ['ar_SA', 'en_US']
const DEFAULT_LANG_FALLBACK = 'ar'

const CHECKOUT_APP_HOST_PATTERN = /https:\/\/checkout-[a-z1-9]*.tamara.co/

const MAP_LANG_TO_LOCALE = {
  en: 'en_US',
  ar: 'ar_SA',
}

const STATUS = {
  UNCHECK: 'uncheck',
  VALID: 'valid',
  INVALID: 'invalid',
}

const QUESTIONS_ORDER_OVERWRITE = [
  ['source_of_income'],
  ['monthly_income'],
  ['monthly_expenses'],
  ['housing_situation', 'number_of_dependents'],
]

const QUESTIONS_OVERWRITE = {
  source_of_income: {
    optionsOrder: [
      'salary_income',
      'government_allowance',
      'business_income',
      'family_allowance',
      'investment_income',
      'student_stipend',
    ],
    en: {
      title: 'Select all your sources of income.',
      options: {
        salary_income: 'Salary',
        business_income: 'Business or self employment',
        investment_income: 'Investments',
        government_allowance: 'Government or Social Security allowance',
        family_allowance: 'Family support',
        student_stipend: 'Student stipend (Mukafa’a)',
      },
    },
    ar: {
      title: 'اختر كل مصادر دخلك',
      options: {
        salary_income: 'راتب',
        business_income: 'عمل خاص أو حر',
        investment_income: 'استثمارات',
        government_allowance: 'دعم حكومي أو ضمان اجتماعي',
        family_allowance: 'مصروف من العائلة',
        student_stipend: 'مكافأة طلابية',
      },
    },
  },
  monthly_income: {
    en: {
      title: "What's your total monthly income?",
    },
    ar: {
      title: 'كم إجمالي دخلك الشهري؟',
    },
  },
  monthly_expenses: {
    en: {
      title: 'How much do you generally spend each month?',
    },
    ar: {
      title: 'كم تنفق عادةً كل شهر؟',
    },
  },
  housing_situation: {
    en: {
      title: 'What is your current housing situation?',
      options: {
        own_property_no_mortgage: 'Own home (no mortgage)',
        own_property_mortgage: 'Own home (with mortgage)',
        rent_temporary_accommodation: 'Renting',
        live_with_relatives: 'Living with family (not paying rent)',
        other: 'Other',
      },
    },
    ar: {
      title: 'ما هو وضع سكنك الحالي؟',
      options: {
        own_property_no_mortgage: 'منزل ملك (بدون رهن)',
        own_property_mortgage: 'منزل ملك (مع رهن)',
        rent_temporary_accommodation: 'مستأجر',
        live_with_relatives: 'سكن مع العائلة (بدون إيجار)',
        other: 'أخرى',
      },
    },
  },
  number_of_dependents: {
    en: {
      title: 'How many people financially rely on you?',
    },
    ar: {
      title: 'كم عدد الأشخاص الذين تعولهم مادياً؟',
    },
  },
}

// Private utils
export const getModifiedRedirectionUrl = (redirectionUrl = '', params = {}) => {
  if (!redirectionUrl) {
    return ''
  }

  const url = new URL(redirectionUrl)

  Object.entries(params).forEach(([key, value]) => {
    url.searchParams.set(key, value)
  })

  const modifiedUrl = url.toString()

  // Support local dev env
  if (Config.isDev) {
    return modifiedUrl.replace(CHECKOUT_APP_HOST_PATTERN, Config.appHostUrl)
  }

  return modifiedUrl
}

const state = {
  data: {},
  submitting: false,
  queryParams: getKycQueryParams() || {},
  status: STATUS.UNCHECK,
  sessionId: getKycSessionId() || '',
  stage: KYC_STAGE.NONE,
  cachedNationalId: getCachedNationalId(),
  cachedDob: getCachedDob(),
  kycSeonSession: '',
  kycToken: getKycTokenFromSessionStorage() || '',
}

const getters = {
  stage(state) {
    return state.stage
  },
  error(state) {
    return state.data?.error || {}
  },
  errorData(_, getters) {
    return getters.error?.data || {}
  },
  isError() {
    return !isEmpty(state.error)
  },
  kycSessionId(state) {
    return state.sessionId || ''
  },
  currentData(state) {
    return state.data || {}
  },
  currentDataMessage(state) {
    return state.data?.message || {}
  },
  customerMaxSpendingLimit(state) {
    return state.data?.message?.customerMaxSpendingLimit || {}
  },
  approvalState(state) {
    return state.data?.message?.approvalState || ''
  },
  expiryDate(state) {
    const expiryDate = state.data?.message?.expiryDate || ''

    if (expiryDate) {
      return parseISO(expiryDate)
    }

    return new Date()
  },
  nafathTransaction(state) {
    return state.data?.message?.nafathRequest || {}
  },
  currency(state) {
    return state.queryParams?.currency || ''
  },
  locale(state) {
    const locale = state.queryParams?.locale || ''
    const [lang] = locale.replace('-', '_').split('_')

    if (SUPPORTED_LOCALE.includes(locale)) {
      return locale
    }

    return MAP_LANG_TO_LOCALE[lang] || DEFAULT_LOCALE_FALLBACK
  },
  lang(_, getters) {
    const [lang] = getters.locale.replace('-', '_').split('_')

    return lang || DEFAULT_LANG_FALLBACK
  },
  clientType(state) {
    return state.queryParams?.clientType
  },
  isSimpleCheckoutEnabled() {
    return (
      state.queryParams?.clientType === SUPPORTED_CLIENT_TYPE.simple_checkout
    )
  },
  componentSource(state) {
    return state.data?.message?.component ?? ''
  },
  cachedNationalId(state) {
    return state.cachedNationalId
  },
  cachedDob(state) {
    return state.cachedDob
  },
  kycSeonSession(state) {
    return state.kycSeonSession
  },
  kycToken(state) {
    return state.kycToken
  },
  isNafathEnabled(_, getters) {
    return getters.currentDataMessage?.isNafathEnabled ?? false
  },
  isSkipNafathEnabled(_, getters) {
    return getters.currentDataMessage?.isSkipNafathEnabled ?? false
  },
  orderInfo(state, getters) {
    return state.data?.message?.orderInfo || getters.errorData?.orderInfo || {}
  },
  // Checkout ID will be appeared on pre-scoring flow
  checkoutId(_, getters) {
    return getters.orderInfo?.checkoutId || ''
  },
  // Checkout ID will be appeared on pre-scoring flow
  redirectionUrl(_, getters) {
    const redirectUrl = getters.orderInfo?.redirectUrl
    const params = { locale: getters.locale }
    return getModifiedRedirectionUrl(redirectUrl, params)
  },
  orderContext(_, getters) {
    return getters.orderInfo?.context || ''
  },
  questions(_, getters) {
    const questions = (getters.currentDataMessage.questions ?? []).filter(
      (question) =>
        !getters.blacklistedQuestionKeys.includes(question?.questionKey)
    )

    if (isFeatureEnabledSafe(abtProvider, FEATURE_GATE_QUESTIONNAIRE_REVAMP)) {
      const filterQuestionKeys = QUESTIONS_ORDER_OVERWRITE.map(
        (list) => list[0]
      )

      return getUpdatedQuestionContent(
        questions
          .filter((question) =>
            filterQuestionKeys.includes(question?.questionKey)
          )
          .sort(
            (a, b) =>
              QUESTIONS_ORDER_OVERWRITE.findIndex(
                (keys) => keys?.indexOf(a?.questionKey) > -1
              ) -
              QUESTIONS_ORDER_OVERWRITE.findIndex(
                (keys) => keys?.indexOf(b?.questionKey) > -1
              )
          ),
        getters?.lang
      )
    }

    return questions
  },
  blacklistedQuestions(_, getters) {
    return (getters.currentDataMessage.questions ?? []).filter((question) =>
      getters.blacklistedQuestionKeys.includes(question?.questionKey)
    )
  },
  blacklistedQuestionKeys() {
    try {
      return isFeatureEnabledSafe(
        abtProvider,
        FEATURE_GATE_QUESTIONNAIRE_REVAMP
      )
        ? QUESTIONS_BLACKLISTED
        : []
    } catch (err) {
      console.error(err)

      return []
    }
  },
  getGroupedQuestions(_, getters) {
    return (questionKey) => {
      const questionKeysList = QUESTIONS_ORDER_OVERWRITE.find(
        (questionKeysList) => questionKeysList[0] === questionKey
      )

      return getUpdatedQuestionContent(
        questionKeysList && questionKeysList?.length > 1
          ? questionKeysList.slice(1).reduce((result, questionKey) => {
              const question = (
                getters.currentDataMessage.questions ?? []
              ).find((question) => question.questionKey === questionKey)

              return question ? result.concat(question) : result
            }, [])
          : [],
        getters?.lang
      )
    }
  },
}

const mutations = {
  SET_DATA: (state, newValue) => {
    state.data = newValue
  },
  SET_STATUS: (state, newValue) => {
    state.status = newValue
  },
  SET_KYC_SESSION_ID: (state, newValue) => {
    state.sessionId = newValue
  },
  SET_STAGE: (state, stage) => {
    if (!isValidStage(stage)) {
      clientLogger.error(LOGGING_EVENTS.ID_VERIFICATION_INVALID_STEP, {
        step: stage ?? '',
      })
      console.error(`Tried to set to an invalid KYC stage: ${stage}`)

      state.stage = KYC_STAGE.NONE

      return
    }

    state.stage = stage
  },
  SET_QUERY_PARAMS: (state, newValue) => {
    state.queryParams = newValue
  },
  SET_CACHED_NATIONAL_ID: (state, nationalId) => {
    state.cachedNationalId = nationalId
  },
  SET_CACHED_DOB: (state, dob) => {
    state.cachedDob = dob
  },
  SET_SUBMITTING(state, value) {
    state.submitting = value
  },
  SET_KYC_SEON_SESSION(state, value) {
    state.kycSeonSession = value
  },
  SET_KYC_TOKEN(state, value) {
    state.kycToken = value
  },
}

const actions = {
  setKycSessionId({ commit }, kycSessionId) {
    commit('SET_KYC_SESSION_ID', kycSessionId)

    setKycSessionId(kycSessionId)
  },
  clearKycSession({ commit }) {
    commit('SET_STATUS', STATUS.UNCHECK)

    removeKycSessionId()
  },
  setQueryParams({ commit }, data) {
    commit('SET_QUERY_PARAMS', data)

    setKycQueryParams(data)
  },
  initKycSession({ commit, dispatch, state }) {
    const token = getKycToken() || ''

    if (token) {
      commit('SET_KYC_TOKEN', token)
      setKycTokenToSessionStorage(token)
      removeKycToken()
    }

    if (!state.kycSeonSession) {
      Seon.getSeonSession()
        .then((data) => {
          if (data) {
            commit('SET_KYC_SEON_SESSION', data)
            dispatch('fetch')
          } else {
            commit('SET_KYC_SEON_SESSION', '')
            clientLogger.error(LOGGING_EVENTS.KYC_SEON_SESSION_FAILURE)
            logger.error('Failed to retrieve kyc SEON session data.', {
              category: 'seon',
            })
          }
        })
        .catch((error) => {
          commit('SET_KYC_SEON_SESSION', '')
          clientLogger.error(LOGGING_EVENTS.KYC_SEON_SESSION_FAILURE)
          logger.error('Failed to retrieve kyc SEON session data.', {
            category: 'seon',
            error: error.message,
          })
        })
      return
    }

    dispatch('fetch')
  },
  async fetch({ commit, rootGetters, getters }, data = {}) {
    const sessionId = getters.kycSessionId

    if (sessionId === '') {
      clientLogger.error(LOGGING_EVENTS.KYC_SESSION_ID_MISSING)
      throw new Error(`KYC Session ID missing!`)
    }

    try {
      const res = await KycApi.fetch(sessionId)
      clientLogger.debug(LOGGING_EVENTS.KYC_FETCH, { ...res })

      const newState = resolveStateFromKycData(res)

      const error = newState?.data?.error || {}

      if (!isEmpty(error)) {
        const { errors = [], title = '', message = '' } = error
        const errorCode = errors[0]?.errorCode
        const errorPayload = {
          title,
          message,
          code: errorCode,
        }
        clientLogger.error(LOGGING_EVENTS.KYC_ERROR, { errorPayload })
      }

      if (getters.status !== STATUS.VALID) {
        commit('SET_STATUS', STATUS.VALID)
      }

      commit('SET_DATA', newState.data)
      commit('SET_STAGE', newState.stage)

      return res
    } catch (err) {
      clientLogger.error(LOGGING_EVENTS.KYC_ERROR, {
        error: isObject(err) ? { ...err } : err,
        sessionId,
      })
      // in case of Nafath polling even if we have error we want to continue polling
      if (data?.isNafathPollingRequest)
        return { step: verificationSteps.ID_VERIFICATION_KSA_NAFATH_WAITING }
      // handle session error
      // ex: when session is expired -> clear data and redirect to main flow ...
      commit('SET_STATUS', STATUS.INVALID)
      removeKycSessionId()

      console.error(err)
    }
  },
  async submitData({ commit, getters, rootGetters }, data = {}) {
    const sessionId = getters.kycSessionId

    if (sessionId === '') {
      throw new Error(`KYC Session ID missing!`)
    }

    if (state.submitting) {
      return
    }

    try {
      commit('SET_SUBMITTING', true)

      const res = await KycApi.submitData(sessionId, data)

      const newState = resolveStateFromKycData(res)

      commit('SET_SUBMITTING', false)
      commit('SET_DATA', newState.data)
      commit('SET_STAGE', newState.stage)

      return res
    } catch (err) {
      clientLogger.error(LOGGING_EVENTS.KYC_ERROR, {
        error: isObject(err) ? { ...err } : err,
        sessionId,
        payload: data,
      })
      commit('SET_SUBMITTING', false)

      throw err
    }
  },
  cacheNationalId({ commit }, nationalId) {
    cacheNationalId(nationalId)

    commit('SET_CACHED_NATIONAL_ID', nationalId)
  },
  cacheDob({ commit }, dob) {
    cacheDob(dob)

    commit('SET_CACHED_DOB', dob)
  },
}

const getUpdatedQuestionContent = (questions, lang) => {
  return questions.map((question) => {
    const overwrite = QUESTIONS_OVERWRITE[question?.questionKey]?.[lang] ?? {}

    const overwriteOptionsOrder =
      QUESTIONS_OVERWRITE[question?.questionKey]?.optionsOrder ?? []

    if (overwrite.title) {
      question.questionTitle = overwrite.title
    }

    if (overwrite.options && (question?.options ?? []).length > 0) {
      question.options = question.options.map((option) => ({
        ...option,
        value: overwrite?.options?.[option?.key] ?? option?.value,
      }))
    }

    if (
      overwriteOptionsOrder.length > 0 &&
      (question?.options ?? []).length > 0
    ) {
      question.options = question.options.sort(
        (a, b) =>
          overwriteOptionsOrder.indexOf(a.key) -
          overwriteOptionsOrder.indexOf(b.key)
      )
    }

    return question
  })
}

export default { namespaced: true, state, getters, mutations, actions }
